import React, { useState, useEffect } from 'react';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { X, MapPin, Clock } from "lucide-react";
import { CommanServices, normalizeIncidentRecipientDirectory } from '@/services/commanServices';
import Combobox from './ui/combobox';
import EscalationRuleBuilder from './EscalationRuleBuilder';
import ConditionalFollowupTrigger from './ConditionalFollowupTrigger';
import RecipientPicker, { resolveRecipients } from './RecipientPicker';
import ScenarioRoleField, { RoleGuidanceBanner, blankRole, buildScenarioRoleOverridePatch, canOverrideScenarioRoles, cleanRoles, markScenarioRolesForOverride, rolesValid, rolesSummary } from './ScenarioRoleField';
import { shouldUseGeoFenceRecipientCreate, timelineWriteSucceeded } from './geoFenceActions';


// Helper to parse timestamps correctly (handles both timezone-aware and naive timestamps)
const parseTimestamp = (timestamp) => {
  if (!timestamp) return null;
  // If timestamp has timezone info (+00:00, Z), use as-is
  if (timestamp.includes('+') || timestamp.includes('Z')) {
    return new Date(timestamp);
  }
  // If no timezone, assume UTC and append 'Z'
  return new Date(timestamp + 'Z');
};

const AcknowledgmentModal = ({ incident, editingItem, mode = 'config', onClose, onSuccess, conditionalContext = null, onAddConditional = null, userRoleMap = {}, prefilledSelections = null, geoFenceTargeting = null, scenarioStaffingDefaults = null, onRoleCatalogUpsert = null, onlineUserIds = null, presenceByUserId = {}, allowedRecipientUserIds = null, allowedRecipientDepartmentIds = null, allowedRecipientTeamIds = null, allowedRecipientCrewIds = null }) => {
  const [formData, setFormData] = useState({
    acknowledgedAt: null, // Changed to null - will be set when Ack button is clicked
    etaMinutes: null,
    etaSource: 'gps', // 'gps' | 'manual'
    modes: [],
    verificationStatus: 'Pending',
    notes: '',
    assessmentNotes: '',
    responseTime: '', // No default SLA — empty/0 means "no SLA entered"
  });

  const [autoFilledUser, setAutoFilledUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [showManualETA, setShowManualETA] = useState(false);
  const [gpsETA, setGpsETA] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [isAcknowledged, setIsAcknowledged] = useState(false); // Track if Ack button was clicked
  const [ackButtonLoading, setAckButtonLoading] = useState(false); // Loading state for Ack button
  const [appUsers, setAppUsers] = useState([]); // List of all app users
  const [escalation, setEscalation] = useState(null); // Phase 2: conditional inject rule
  const [manuallySelectedUser, setManuallySelectedUser] = useState(null); // Manually selected user
  const [showManualSelection, setShowManualSelection] = useState(false); // Show manual selection dropdown
  const [roles, setRoles] = useState([blankRole()]);
  const [note, setNote] = useState('');
  const [roleOverrideMeta, setRoleOverrideMeta] = useState({});
  const recipientUserAllowed = (userId) => !Array.isArray(allowedRecipientUserIds)
    || allowedRecipientUserIds.map(String).includes(String(userId));
  const recipientScopeRestricted = [
    allowedRecipientUserIds,
    allowedRecipientDepartmentIds,
    allowedRecipientTeamIds,
    allowedRecipientCrewIds,
  ].some(Array.isArray);

  // Requested-stage exercises author injects exactly like the Scenario page (full role-based layout).
  // Role-planning mode: scenario page, exercise at Requested, or a REAL incident
  // reviewing its scenario plan (cards join the plan and fire on activation).
  const isScenario = !!incident?.is_scenario || (!!incident?.is_exercise && String(incident?.status || '').toLowerCase() === 'requested') || (!incident?.is_exercise && incident?.plan_state === 'planned');
  // Real incident reviewing its plan: pickers stay live, save = real verification card
  const realPlanReview = !incident?.is_exercise && incident?.plan_state === 'planned';
  // + button on a REAL incident: multi-recipient picker (per-row role/SLA),
  // one verification card per resolved user; no planning/conditional blocks.
  const realCreate = !editingItem && !incident?.is_exercise && !incident?.is_scenario;
  // Pen on a scenario-planned card of a REAL incident: role editor only
  // (editable roles/count, read-only instructions, SLA optional: 0 = no SLA).
  const planCardEdit = !!editingItem && editingItem?.plan_state === 'planned' && !incident?.is_exercise;
  const scenarioPulledEdit = canOverrideScenarioRoles(incident, editingItem);
  // Geo-fence (and similar flows) hand in pre-selected recipients.
  const [recipientSelections, setRecipientSelections] = useState(() => (Array.isArray(prefilledSelections) ? prefilledSelections : []));
  const geoFenceRecipientCreate = shouldUseGeoFenceRecipientCreate(editingItem, geoFenceTargeting, recipientSelections);
  const recipientCreate = realCreate || geoFenceRecipientCreate;
  const geoFenceMeta = geoFenceRecipientCreate ? {
    source: 'geo_fence',
    geoFenceTargeting,
  } : {};
  const isActiveExercise = !!incident?.is_exercise && String(incident?.status || '').toLowerCase() === 'active';
  const isRequested = false;

  // Prefill role(s) + note from the source card when authoring a conditional follow-up.
  useEffect(() => {
    if (!editingItem && conditionalContext?.sourceRoles?.length) {
      setRoles(conditionalContext.sourceRoles.map((r) => ({ ...r, sla: r.sla == null ? '' : r.sla })));
      if (conditionalContext.sourceNote) setNote(conditionalContext.sourceNote);
    }
  }, [conditionalContext, editingItem]);

  // Initialize form data from editingItem if in edit mode

  useEffect(() => {
    if (editingItem) {
      // Fetch fresh data from api to ensure we have latest values
      const fetchFreshData = async () => {
        try {
          const response = await CommanServices.getincidentTimeline(incident.id)
          if (response.status) {
            const timelineItems = await response.data;
            const freshItem = timelineItems.find(item => item.id === editingItem.id);
            
            if (freshItem) {
              console.log('✅ Fetched fresh timeline item:', freshItem);
              initializeFormFromItem(freshItem);
            } else {
              console.warn('⚠️ Could not find fresh item, using passed editingItem');
              initializeFormFromItem(editingItem);
            }
          } else {
            console.warn('⚠️ Failed to fetch timeline, using passed editingItem');
            initializeFormFromItem(editingItem);
          }
        } catch (error) {
          console.error('⚠️ Error fetching fresh data:', error);
          initializeFormFromItem(editingItem);
        }
      };

      fetchFreshData();
    } else {
      setIsEditMode(false);
      if (!isScenario || realPlanReview) autoFillAcknowledgedBy();
    }
  }, [editingItem]);

  // Helper function to initialize form from timeline item
  const initializeFormFromItem = (item) => {
    setIsEditMode(true);
    const meta = item.meta || {};
    setRoleOverrideMeta(meta);
    
    console.log('🔍 Initializing form from item, meta:', meta);
    console.log('🔍 Verification Status:', meta.verificationStatus);
    
    // Check if already acknowledged
    const hasAcknowledged = !!meta.acknowledgedAt;
    setIsAcknowledged(hasAcknowledged);
    
    const newFormData = {
      acknowledgedAt: meta.acknowledgedAt || null,
      etaMinutes: meta.etaMinutes || null,
      etaSource: meta.etaSource || 'gps',
      modes: meta.modes || [],
      verificationStatus: meta.verificationStatus || 'Pending',
      notes: meta.notes || '',
      assessmentNotes: meta.assessmentNotes || '',
      responseTime: meta.sla?.withinMinutes ?? '',
    };
    
    console.log('🔍 Setting formData:', newFormData);
    setFormData(newFormData);
    setEscalation(meta.escalation || null);
    setRoles(Array.isArray(meta.scenarioRoles) && meta.scenarioRoles.length
      ? markScenarioRolesForOverride(meta.scenarioRoles.map((r) => ({ ...r, sla: r.sla == null ? '' : r.sla })), canOverrideScenarioRoles(incident, item))
      : [blankRole()]);
    setNote(meta.assignmentNote || '');
    
    // Set the acknowledged by user from the existing item
    if (meta.acknowledgedBy) {
      setAutoFilledUser({
        userId: meta.acknowledgedBy.userId,
        userName: meta.acknowledgedBy.userName,
        crewName: meta.acknowledgedBy.crewName,
        teamName: meta.acknowledgedBy.teamName,
        withinMinutes: meta.sla?.withinMinutes ?? null
      });
    }
    
    setLoadingUser(false);
    setShowManualETA(meta.etaSource === 'manual' || (meta.etaMinutes && !meta.etaSource));
  };

  // Fetch all app users on mount
  useEffect(() => {
    fetchAppUsers();
  }, [incident?.id, recipientScopeRestricted]);

  const fetchAppUsers = async () => {
    try {
      if (recipientScopeRestricted && incident?.id) {
        const response = await CommanServices.getIncidentRecipientDirectory(incident.id);
        setAppUsers(normalizeIncidentRecipientDirectory(response).users);
        return;
      }
      const response = await CommanServices.getAppUsers();
      if (response.status) setAppUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching app users:', error);
      setAppUsers([]);
    }
  };

  // Auto-fill acknowledged by user on mount (only for new acknowledgments)
  useEffect(() => {
    if (!editingItem && (!isScenario || realPlanReview)) {
      autoFillAcknowledgedBy();
    }
  }, [incident]);

  const autoFillAcknowledgedBy = async () => {
    try {
      setLoadingUser(true);
      
      const requestPayload = {
        incidentType: incident.incident_type,
        event: incident.event,
        trigger: incident.trigger,
        locationPath: incident.location || []
      };
      const response = await CommanServices.teamsMatchedData(requestPayload)

      if (response.status) {
        const data = await response.data;
        console.log('📋 DEBUG - Received from /api/teams/match:', data);
        
        const candidates = data.candidates || [];
        const hasRealMatch = data.matchedTeams > 0 && data.matchedCrews > 0;
        
        // Only auto-select if there's a REAL team/crew match (not fallback)
        if (!hasRealMatch) {
          // No team/crew match — let the user pick any exercise participant manually (no blocking error).
          setError('');
        }
        
        // Sort candidates by escalation level and response time
        const sortedCandidates = candidates.filter(c => (
          c.crewId && c.teamId && recipientUserAllowed(c.userId)
        )).sort((a, b) => {
          if (a.escalationLevel !== b.escalationLevel) {
            return a.escalationLevel - b.escalationLevel;
          }
          return a.withinMinutes - b.withinMinutes;
        });
        
        if (sortedCandidates.length > 0) {
          // Store ALL matching candidates (not just first one)
          window.allMatchingCandidates = sortedCandidates;
          
          const selectedUser = sortedCandidates[0];
          setAutoFilledUser(selectedUser);
          
          // Mock GPS ETA calculation (5-15 min random)
          const mockETA = Math.floor(Math.random() * 10) + 5;
          setGpsETA(mockETA);
          setFormData(prev => ({
            ...prev,
            etaMinutes: mockETA,
            etaSource: 'gps'
          }));
          
          console.log(`✅ Auto-filled with ${sortedCandidates.length} matching users/teams/crews`);
        } else {
          console.error('❌ No valid candidates with crew/team found');
          setError('');
        }
      } else {
        const errorText = await response.message;
        console.error('❌ api Error:', response.status, errorText);
        // setError('Failed to load user information');
      }
    } catch (error) {
      console.error('❌ Error auto-filling user:', error);
      setError('Failed to load user information');
    } finally {
      setLoadingUser(false);
    }
  };

  const toggleMode = (mode) => {
    setFormData(prev => ({
      ...prev,
      modes: prev.modes.includes(mode)
        ? prev.modes.filter(m => m !== mode)
        : [...prev.modes, mode]
    }));
  };

  // Handle Ack button click - records server time ONLY, does NOT change verification status
  const handleAck = async () => {
    if (!autoFilledUser) {
      setError('No user assigned for acknowledgment');
      return;
    }

    setAckButtonLoading(true);
    try {
      // Get current server time
      const acknowledgedAt = new Date().toISOString();
      
      // Calculate SLA using parseTimestamp
      const incidentReportedAt = parseTimestamp(editingItem?.meta?.notifiedAt || incident.created_at);
      const ackTime = new Date(acknowledgedAt);
      const responseTimeMinutes = Math.floor((ackTime - incidentReportedAt) / 60000);
      // No SLA entered → no deadline: an acknowledgment can never be late.
      const slaWithinMinutes = autoFilledUser.withinMinutes || null;
      const slaMet = slaWithinMinutes == null ? true : responseTimeMinutes <= slaWithinMinutes;
      
      // Update ONLY acknowledgedAt - do NOT touch verificationStatus
      setFormData(prev => ({
        ...prev,
        acknowledgedAt
        // verificationStatus stays as-is (Pending by default)
      }));
      
      setIsAcknowledged(true);
      
      // Show success feedback
      console.log(`✅ Acknowledged at ${acknowledgedAt}`);
      console.log(`📊 Acknowledgment Status: ${slaMet ? 'ACK (within)' : 'ACK late'}`);
      console.log(`📊 SLA: ${responseTimeMinutes}m ${slaMet ? '≤' : '>'} ${slaWithinMinutes}m`);
      
    } catch (error) {
      console.error('Error recording acknowledgment:', error);
      setError('Failed to record acknowledgment');
    } finally {
      setAckButtonLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isScenario && (planCardEdit || !realPlanReview)) {
      if (!rolesValid(roles, incident?.is_scenario ? scenarioStaffingDefaults : null)) {
        setError('Add at least one role.');
        return;
      }
      setLoading(true);
      setError('');
      try {
        const overridePatch = scenarioPulledEdit
          ? buildScenarioRoleOverridePatch(roleOverrideMeta, roles)
          : null;
        if (overridePatch?.error) {
          setError(overridePatch.error);
          setLoading(false);
          return;
        }
        const clean = overridePatch?.scenarioRoles || cleanRoles(roles);
        const meta = {
          scenarioRoles: clean,
          ...(overridePatch ? {
            roleAssignments: overridePatch.roleAssignments,
            history: overridePatch.history,
            roleOverrideHistory: overridePatch.roleOverrideHistory,
          } : {}),
          assignmentNote: note || '',
          verificationStatus: 'Pending',
          // Empty or 0 = no SLA entered → store none (no 5-minute fallback).
          sla: parseInt(formData.responseTime) > 0 ? { withinMinutes: parseInt(formData.responseTime) } : null,
          escalation: escalation || null,
          ...(!overridePatch ? { history: Array.isArray(editingItem?.meta?.history) ? editingItem.meta.history : [] } : {}),
          ...(conditionalContext?.conditional ? { conditional: conditionalContext.conditional } : {}),
        };
        const base = {
          incident_id: incident.id,
          group: 'acknowledgment',
          timestamp: new Date().toISOString(),
          actor: { id: 'role', name: rolesSummary(clean), type: 'role' },
          title: 'Verification (role-based)',
          details: rolesSummary(clean),
          chips: [],
          meta,
        };
        let response;
        if (isEditMode && editingItem) {
          response = await CommanServices.updatePutIncidentTimeline(incident.id, editingItem.id, {
            ...editingItem,
            ...base,
            meta: { ...(editingItem.meta || {}), ...meta },
          });
        } else {
          response = await CommanServices.addincidentTimeline(incident.id, base);
        }
        if (!timelineWriteSucceeded(response)) throw new Error(response?.message || 'Failed to save verification');
        if (onSuccess) onSuccess();
        onClose();
      } catch (error) {
        console.error('Scenario role verification error:', error);
        setError(error.message || 'Failed to save roles');
      } finally {
        setLoading(false);
      }
      return;
    }

    // A new (create-mode) verification still needs an assignee, but a takeover/edit of an existing
    // role-based verification has no single assignee — let the admin act on its behalf.
    if (!autoFilledUser && !(isEditMode && editingItem) && !recipientCreate) {
      setError('No user assigned for acknowledgment');
      return;
    }
    if (!editingItem && !recipientCreate && autoFilledUser && !recipientUserAllowed(autoFilledUser.userId)) {
      setError('The selected user is outside your incident scope.');
      return;
    }
    const storedUser = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch (_) { return {}; } })();
    const actingUser = autoFilledUser || {
      userId: storedUser.id || storedUser._id || 'admin',
      userName: storedUser.full_name || storedUser.name || 'Admin (takeover)',
      withinMinutes: parseInt(formData.responseTime) || editingItem?.meta?.sla?.withinMinutes || null,
      crewName: '',
      teamName: '',
    };

    // No longer require acknowledgedAt upfront - it can be set later with Ack button
    // Users can save the form with other info first, then Ack later

    if (showManualETA && !formData.etaMinutes) {
      setError('Please enter ETA in minutes');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Use acknowledgedAt if set (via Ack button), otherwise use current time as fallback for edit mode
      const acknowledgedAt = formData.acknowledgedAt || new Date().toISOString();
      const incidentReportedAt = parseTimestamp(editingItem?.meta?.notifiedAt || incident.created_at);
      const ackTime = new Date(acknowledgedAt);
      const responseTimeMinutes = Math.floor((ackTime - incidentReportedAt) / 60000);
      
      // EDIT MODE: Update existing acknowledgment
      if (isEditMode && editingItem) {
        console.log('📝 Updating existing acknowledgment...');
        
        const oldMeta = editingItem.meta || {};
        // No SLA entered → no deadline: never counted late.
        const slaWithinMinutes = parseInt(formData.responseTime) || oldMeta.sla?.withinMinutes || null;
        const slaMet = slaWithinMinutes == null ? true : responseTimeMinutes <= slaWithinMinutes;
        
        // Build change history
        const changes = [];
        const timestamp = new Date().toISOString();
        const editedBy = {
          userId: actingUser.userId,
          userName: actingUser.userName
        };
        
        // Track Acknowledged At changes (when Ack button is pressed)
        if (formData.acknowledgedAt && formData.acknowledgedAt !== oldMeta.acknowledgedAt) {
          // Calculate acknowledgment status using parseTimestamp
          const incidentReportedAt = parseTimestamp(editingItem?.meta?.notifiedAt || incident.created_at);
          const ackTime = new Date(formData.acknowledgedAt);
          const responseTimeMinutes = Math.floor((ackTime - incidentReportedAt) / 60000);
          const slaWithinMinutes = actingUser.withinMinutes || null;
          const slaMet = slaWithinMinutes == null ? true : responseTimeMinutes <= slaWithinMinutes;
          
          const ackStatus = slaMet 
            ? 'ACK (within)' 
            : `ACK late (${responseTimeMinutes}m > ${slaWithinMinutes}m)`;
          
          changes.push({
            field: 'Acknowledged At',
            from: oldMeta.acknowledgedAt ? new Date(oldMeta.acknowledgedAt).toLocaleString() : '—',
            to: new Date(formData.acknowledgedAt).toLocaleString(),
            timestamp,
            editedBy,
            autoAck: !oldMeta.acknowledgedAt // Mark if this was the initial Ack
          });
          
          // Also track Acknowledgment Status change
          changes.push({
            field: 'Acknowledgment Status',
            from: 'Pending',
            to: ackStatus,
            timestamp,
            editedBy
          });
        }
        
        // Track Verification Status changes (independent from Ack)
        let verifiedAt = oldMeta.verifiedAt; // Preserve existing verifiedAt
        let acknowledgedAt = formData.acknowledgedAt; // Start with form value
        let slaMet_updated = slaMet; // Start with existing slaMet calculation
        
        if (formData.verificationStatus !== oldMeta.verificationStatus) {
          changes.push({
            field: 'Verification Status',
            from: oldMeta.verificationStatus,
            to: formData.verificationStatus,
            timestamp,
            editedBy
          });
          
          // When verification is finalized (Confirmed or False Alarm)
          if (formData.verificationStatus === 'Confirmed' || formData.verificationStatus === 'False Alarm') {
            // Set verifiedAt timestamp when verification status changes from Pending
            if (oldMeta.verificationStatus === 'Pending') {
              verifiedAt = new Date().toISOString();
            }
            
            // Act like "Ack" button: Set acknowledgedAt if missing
            if (!acknowledgedAt) {
              acknowledgedAt = new Date().toISOString();
              
              // Recalculate slaMet with the new acknowledgedAt
              const incidentReportedAt = parseTimestamp(editingItem?.meta?.notifiedAt || incident.created_at);
              const ackTime = new Date(acknowledgedAt);
              const responseTimeMinutes = Math.floor((ackTime - incidentReportedAt) / 60000);
              slaMet_updated = responseTimeMinutes <= slaWithinMinutes;
              
              const ackStatus = slaMet_updated
                ? 'ACK (within)' 
                : `ACK late (${responseTimeMinutes}m > ${slaWithinMinutes}m)`;
              
              changes.push({
                field: 'Acknowledgment Status',
                from: 'Pending',
                to: ackStatus,
                timestamp,
                editedBy
              });
            }
          }
        }
        if (formData.etaMinutes !== oldMeta.etaMinutes) {
          changes.push({
            field: 'ETA',
            from: oldMeta.etaMinutes ? `${oldMeta.etaMinutes} min` : 'None',
            to: formData.etaMinutes ? `${formData.etaMinutes} min` : 'None',
            timestamp,
            editedBy
          });
        }
        if (JSON.stringify(formData.modes) !== JSON.stringify(oldMeta.modes || [])) {
          changes.push({
            field: 'Notification Modes',
            from: (oldMeta.modes || []).join(', ') || 'None',
            to: formData.modes.join(', ') || 'None',
            timestamp,
            editedBy
          });
        }
        if (formData.notes !== oldMeta.notes) {
          changes.push({
            field: 'Notes',
            from: oldMeta.notes || 'None',
            to: formData.notes || 'None',
            timestamp,
            editedBy
          });
        }
        if (formData.assessmentNotes !== oldMeta.assessmentNotes) {
          changes.push({
            field: 'Assessment Notes',
            from: oldMeta.assessmentNotes || 'None',
            to: formData.assessmentNotes || 'None',
            timestamp,
            editedBy
          });
        }

        // Update the timeline entry (preserve notifiedAt and escalationLevel)
        const updatedEntry = {
          ...editingItem,
          actor: actingUser ? { id: actingUser.userId, name: actingUser.userName, type: 'user' } : editingItem.actor,
          chips: [
            slaMet_updated ? 'On Time' : 'Late',
            formData.verificationStatus
          ],
          meta: {
            ...oldMeta,
            notifiedAt: oldMeta.notifiedAt,  // Preserve notifiedAt
            acknowledgedAt: acknowledgedAt,  // Use local variable (may be auto-set)
            verifiedAt: verifiedAt,  // Set verifiedAt timestamp
            escalationLevel: oldMeta.escalationLevel,  // Preserve escalationLevel
            isEscalated: oldMeta.isEscalated,  // Preserve isEscalated flag
            acknowledgedBy: autoFilledUser ? {
              userId: actingUser.userId,
              userName: actingUser.userName,
              crewName: actingUser.crewName || '',
              teamName: actingUser.teamName || ''
            } : oldMeta.acknowledgedBy,
            etaMinutes: formData.etaMinutes,
            etaSource: formData.etaSource,
            modes: formData.modes,
            verificationStatus: formData.verificationStatus,
            notes: formData.notes,
            assessmentNotes: formData.assessmentNotes,
            sla: {
              withinMinutes: slaWithinMinutes,
              met: slaMet_updated,
              actualMinutes: responseTimeMinutes,
              responseTime: formData.responseTime
            },
            escalation: escalation || null,
            history: [...(oldMeta.history || []), ...changes],
            updated_at: timestamp
          }
        };

        const response = await CommanServices.updatePutIncidentTimeline(incident.id, editingItem.id, updatedEntry)

        if (!timelineWriteSucceeded(response)) {
          throw new Error('Failed to update acknowledgment');
        }
        
        console.log('✅ Successfully updated acknowledgment');

        // Supersede-on-verify is RETIRED (web + app): every assignee keeps
        // their own live verification card — one person's verify never
        // closes anyone else's, and a user can receive and answer multiple
        // verification cards over the incident's lifetime.
      }
      // CREATE MODE on a REAL incident: one verification card per resolved recipient
      else if (recipientCreate) {
        const recips = await resolveRecipients(recipientSelections, {
          incidentId: incident?.id,
          restricted: [
            allowedRecipientUserIds,
            allowedRecipientDepartmentIds,
            allowedRecipientTeamIds,
            allowedRecipientCrewIds,
          ].some(Array.isArray),
        });
        if (recips.length === 0) {
          throw new Error('Choose at least one recipient: user, department, or crew.');
        }
        const existingAckCards = incident.timeline_items?.filter(item => item.group === 'acknowledgment') || [];
        let escLevel = Math.max(0, ...existingAckCards.map(card => card.meta?.escalationLevel || 0));
        for (const rec of recips) {
          escLevel += 1;
          // No SLA entered for this manually added user → none (no fallback).
          const slaWithinMinutes = parseInt(rec.sla) || parseInt(formData.responseTime) || null;
          const slaMet = slaWithinMinutes == null ? true : responseTimeMinutes <= slaWithinMinutes;
          const timelineEntry = {
            incident_id: incident.id,
            group: 'acknowledgment',
            actor: { id: rec.userId, name: rec.userName, type: 'user' },
            title: 'Incident Acknowledged',
            details: formData.notes || `Verification assigned to ${rec.userName}`,
            chips: [slaMet ? 'On Time' : 'Late', formData.verificationStatus],
            meta: {
              notifiedAt: new Date().toISOString(),
              escalationLevel: escLevel,
              manuallyCreated: true,
              addedDuringActive: isActiveExercise,
              acknowledgedAt: formData.acknowledgedAt,
              verifiedAt: (formData.verificationStatus === 'Confirmed' || formData.verificationStatus === 'False Alarm') ? new Date().toISOString() : null,
              acknowledgedBy: { userId: rec.userId, userName: rec.userName, crewName: '', teamName: '' },
              assignedUserIds: [String(rec.userId)],
              assignees: [{ userId: String(rec.userId), userName: rec.userName, crewName: '', teamName: '' }],
              ...(rec.sourceType ? { sourceGroup: { type: rec.sourceType, name: rec.sourceName } } : {}),
              ...(String(rec.role || '').trim() ? {
                role: String(rec.role).trim(),
                roleAssignments: { [String(rec.role).trim()]: [{ type: 'user', id: String(rec.userId), name: rec.userName }] },
              } : {}),
              etaMinutes: formData.etaMinutes,
              etaSource: formData.etaSource,
              modes: formData.modes,
              verificationStatus: formData.verificationStatus,
              notes: formData.notes,
              assessmentNotes: formData.assessmentNotes,
              sla: slaWithinMinutes == null
                ? null
                : { withinMinutes: slaWithinMinutes, met: slaMet, actualMinutes: responseTimeMinutes, responseTime: String(slaWithinMinutes) },
              escalation: null,
              history: [],
              ...geoFenceMeta,
              ...(geoFenceRecipientCreate ? { recipientIds: [String(rec.userId)] } : {}),
            }
          };
          const response = await CommanServices.addincidentTimeline(incident.id, timelineEntry);
          if (!timelineWriteSucceeded(response)) {
            throw new Error(`Failed to save verification for ${rec.userName}`);
          }
        }
      }
      // CREATE MODE: Create new acknowledgment for the manually selected user
      else {
        // Use only the manually selected user (single card creation)
        const candidate = autoFilledUser;
        
        if (!candidate) {
          throw new Error('No user selected');
        }
        
        console.log(`📤 Creating 1 acknowledgment entry for ${candidate.userName}...`);
        
        // Empty or 0 = no SLA entered → none (no fallback, never late).
        const slaWithinMinutes = parseInt(formData.responseTime) || null;

        const slaMet = slaWithinMinutes == null ? true : responseTimeMinutes <= slaWithinMinutes;

        // Get all existing acknowledgment cards to determine escalation level
        const existingAckCards = incident.timeline_items?.filter(item => item.group === 'acknowledgment') || [];
        const maxEscalationLevel = Math.max(0, ...existingAckCards.map(card => card.meta?.escalationLevel || 0));
        const newEscalationLevel = maxEscalationLevel + 1;

        const timelineEntry = {
          incident_id: incident.id,
          group: 'acknowledgment',
          actor: {
            id: candidate.userId,
            name: candidate.userName,
            type: 'user'
          },
          title: 'Incident Acknowledged',
          details: formData.notes || `Acknowledged by ${candidate.userName}`,
          chips: [
            slaMet ? 'On Time' : 'Late',
            formData.verificationStatus
          ],
          meta: {
            notifiedAt: new Date().toISOString(), // Set notifiedAt to current time for manually created cards
            escalationLevel: newEscalationLevel, // Increment escalation level based on existing cards
            manuallyCreated: true,
            addedDuringActive: isActiveExercise,
            acknowledgedAt: formData.acknowledgedAt,
            verifiedAt: (formData.verificationStatus === 'Confirmed' || formData.verificationStatus === 'False Alarm') ? new Date().toISOString() : null,
            acknowledgedBy: {
              userId: candidate.userId,
              userName: candidate.userName,
              crewName: candidate.crewName || '',
              teamName: candidate.teamName || ''
            },
            etaMinutes: formData.etaMinutes,
            etaSource: formData.etaSource,
            modes: formData.modes,
            verificationStatus: formData.verificationStatus,
            notes: formData.notes,
            assessmentNotes: formData.assessmentNotes,
            sla: slaWithinMinutes == null ? null : {
              withinMinutes: slaWithinMinutes,
              met: slaMet,
              actualMinutes: responseTimeMinutes,
              responseTime: formData.responseTime
            },
            escalation: escalation || null,
            history: []
          }
        };

        const response = await CommanServices.addincidentTimeline(incident.id,timelineEntry)

        if (!timelineWriteSucceeded(response)) {
          throw new Error(`Failed to save acknowledgment for ${candidate.userName}`);
        }
        
        console.log(`✅ Successfully created 1 acknowledgment entry for ${candidate.userName}`);
      }
      
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving acknowledgment:', error);
      setError(error.message || 'Failed to save acknowledgment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-900">Verifications</h3>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close verifications">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {isScenario && !recipientCreate && (
            <ScenarioRoleField roles={roles} setRoles={setRoles} note={note} setNote={setNote} slaValue={formData.responseTime} onSlaChange={(v) => setFormData((prev) => ({ ...prev, responseTime: v }))} slaLabel="SLA — Response Time (minutes)" hideIntro={planCardEdit} slaZeroLabel={'(0 or empty = no SLA)'} roleCatalog={incident?.is_scenario ? scenarioStaffingDefaults : null} onRoleCatalogUpsert={onRoleCatalogUpsert} lockScenarioDefaultRoles={scenarioPulledEdit} roleAssignments={roleOverrideMeta?.roleAssignments || {}} />
          )}
          {isRequested && (
            <RoleGuidanceBanner roles={editingItem?.meta?.scenarioRoles} note={editingItem?.meta?.assignmentNote} />
          )}
          {/* Inject config: assignee + SLA + conditional logic (hidden in takeover mode) */}
          {mode !== 'takeover' && (
            <div className="space-y-6">
              {recipientCreate && (
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-sm font-semibold text-slate-900">Recipients</Label>
                    <p className="text-xs leading-5 text-slate-500">Select people, departments, teams, or crews. Each resolved person receives their own verification card.</p>
                  </div>
                  <RecipientPicker
                    selections={recipientSelections}
                    onChange={setRecipientSelections}
                    testIdPrefix="ack"
                    incidentId={incident?.id}
                    onlineUserIds={onlineUserIds}
                    presenceByUserId={presenceByUserId}
                    variant="notification"
                    allowedUserIds={allowedRecipientUserIds}
                    allowedDepartmentIds={allowedRecipientDepartmentIds}
                    allowedTeamIds={allowedRecipientTeamIds}
                    allowedCrewIds={allowedRecipientCrewIds}
                  />
                </div>
              )}
              {/* User Selection */}
              {!incident?.is_scenario && !recipientCreate && !planCardEdit && (
              <div className={`space-y-2 p-4 bg-blue-50 border border-blue-200 rounded-lg ${isScenario && !realPlanReview ? 'opacity-50 pointer-events-none select-none' : ''}`}>
                {isScenario && !realPlanReview && (
                  <p className="text-[11px] text-violet-600 font-medium">Assignees are configured through Required Roles and Suggested staffing while planning.</p>
                )}
                <Label className="text-sm font-medium text-blue-900">Assigned User</Label>
                <p className="text-xs text-blue-700 mb-2">{editingItem ? 'Change the user assigned to this verification card' : 'Choose a user to create a verification card for'}</p>
                <Combobox
                  value={manuallySelectedUser?.id || autoFilledUser?.userId || ''}
                  onValueChange={(userId) => {
                    const selected = appUsers.find(u => u.id === userId);
                    if (selected) {
                      setManuallySelectedUser(selected);
                      setAutoFilledUser({
                        userId: selected.id,
                        userName: selected.name,
                        crewName: '',
                        teamName: ''
                      });
                    }
                  }}
                  placeholder="Type or select user..."
                  options={appUsers?.filter((user) => recipientUserAllowed(user.id)).map((user) => {
                    const role = userRoleMap[String(user.id)] || userRoleMap[String(user.name)] || user.position;
                    return {
                      value: user.id,
                      label: `${user.name}${role ? ` — ${role}` : ''}${onlineUserIds?.has?.(String(user.id)) ? ' · Online now' : ''}`
                    };
                  })}
                  createLabel="Create value"
                  className="border-gray-300" 
                  is_create={false}
                />
                {manuallySelectedUser && (
                  <div className="mt-2 p-3 bg-white border border-blue-300 rounded-md">
                    <p className="text-sm font-medium text-blue-900">{manuallySelectedUser.name}</p>
                    {(() => {
                      const role = userRoleMap[String(manuallySelectedUser.id)] || userRoleMap[String(manuallySelectedUser.name)] || manuallySelectedUser.position;
                      return role ? (
                        <p className="text-xs text-emerald-700 font-medium mt-1">Role: {role}</p>
                      ) : null;
                    })()}
                  </div>
                )}
              </div>
              )}

              {/* SLA (Response Time) */}
              {!recipientCreate && !planCardEdit && (!isScenario || realPlanReview) && (
              <div className="space-y-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <Label className="text-sm font-medium text-amber-900">SLA (Response Time - minutes)</Label>
                <p className="text-xs text-amber-700 mb-2">Set the expected response time for this verification</p>
                <Input
                  type="number"
                  min="0"
                  value={formData.responseTime}
                  onChange={(e) => setFormData(prev => ({...prev, responseTime: e.target.value}))}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                      e.preventDefault();
                    }
                  }}
                  placeholder="Enter minutes..."
                  className="mt-1 no-number-spinner"
                />
              </div>
              )}
            </div>
          )}

          {/* Conditional follow-ups (scenario planning) */}
          {mode !== 'takeover' && isScenario && !recipientCreate && (
            <ConditionalFollowupTrigger editingItem={editingItem} onAddConditional={onAddConditional} />
          )}
          {/* Phase 2: Conditional escalation rule (hidden in takeover mode and during the active stage) */}
          {mode !== 'takeover' && !isScenario && !isActiveExercise && !recipientCreate && (
            <EscalationRuleBuilder value={escalation} onChange={setEscalation} />
          )}

          {/* TAKEOVER MODE: show ONLY the Verification Status (act on behalf of the user) */}
          {mode === 'takeover' && (
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Verification Status</Label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({...prev, verificationStatus: 'Confirmed'}))}
                  className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${
                    formData.verificationStatus === 'Confirmed'
                      ? 'bg-green-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  ✓ Confirmed
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({...prev, verificationStatus: 'False Alarm'}))}
                  className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${
                    formData.verificationStatus === 'False Alarm'
                      ? 'bg-red-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  ✗ False Alarm
                </button>
              </div>
            </div>
          )}

          {/* Actions */}
          {recipientCreate ? (
            <div className="flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm font-medium text-slate-500">
                {recipientSelections.length} recipient {recipientSelections.length === 1 ? 'selection' : 'selections'}
              </p>
              <div className="flex items-center justify-end gap-3">
                <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-violet-600 text-white hover:bg-violet-700"
                  disabled={loading || recipientSelections.length === 0}
                >
                  {loading ? 'Creating...' : `Create (${recipientSelections.length})`}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={loading || ((isScenario && (planCardEdit || !realPlanReview)) ? !rolesValid(roles, incident?.is_scenario ? scenarioStaffingDefaults : null) : !autoFilledUser)}>
                {loading ? 'Saving...' : ((isScenario && (planCardEdit || !realPlanReview)) ? 'Save roles' : (realPlanReview ? 'Send' : 'Save Verification'))}
              </Button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default AcknowledgmentModal;
